import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

type ManuscriptCountLineChartData = {
  points: Array<{
    label: string;
    count: number;
  }>;
};

interface Props {
  data: ManuscriptCountLineChartData;
  axes?: {
    x?: string;
    y?: string;
  };
}

const LINE_STROKE = '#EA580C';

export const ManuscriptCountLineChart = ({ data, axes }: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const xAxisHeight = isMobile ? 44 : 30;
  const chartHeight = isMobile ? (axes?.x ? 248 : 272) : axes?.x ? 276 : 300;

  return (
    <Box>
      <Flex align="stretch">
        {axes?.y ? (
          <Flex align="center" justify="center" pr={2} minW="28px">
            <Text
              textStyle="textContext"
              textAlign="center"
              sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {axes.y}
            </Text>
          </Flex>
        ) : null}
        <Box flex="1">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart
              data={data.points}
              margin={{
                top: 16,
                right: isMobile ? 8 : 16,
                left: isMobile ? 0 : 16,
                bottom: isMobile ? 12 : 8
              }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#D9E2EC"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                height={xAxisHeight}
                interval="preserveStartEnd"
                minTickGap={isMobile ? 20 : 12}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={isMobile ? 32 : 40}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #CBD5E1',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
                  backgroundColor: '#FFFFFF'
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={LINE_STROKE}
                strokeWidth={3}
                dot={
                  isMobile
                    ? false
                    : {
                        r: 4,
                        fill: LINE_STROKE,
                        stroke: '#FFFFFF',
                        strokeWidth: 2
                      }
                }
                activeDot={{
                  r: 6,
                  fill: '#FFFFFF',
                  stroke: LINE_STROKE,
                  strokeWidth: 2
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
      {axes?.x ? (
        <Text mt={2} textAlign="center" textStyle="textContext">
          {axes.x}
        </Text>
      ) : null}
    </Box>
  );
};
